import * as React from "react";
import { getRUM } from '@codametrix/client-observability';
import {
    Typography,
    Popover,
    Box,
    Button,
    Input,
    TextField,
    Divider,
    Switch,
    Tooltip,
    MenuItem,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    IconButton,
    Grid,
    Select
} from "@mui/material";
import {
    buildSearchFilter,
    caseContext,
    defaultColumns,
    getColId,
    ViewType,
} from "../../organisms/workItemsTable";
import { FieldsMenu } from "../fields";
import theme, { scrollStyles } from "../../../theme/Theme";
import global from "../../../../public/assets/icons/global.svg";
import TabList from "@mui/lab/TabList";
import TabContext from "@mui/lab/TabContext";
import "./index.css";
import {
    policiesContext,
    serviceLineMapper,
    WidgetConstants,
} from "../../../utils/common";
import lockIcon from "../../../../public/assets/icons/lock_custom.svg";
import { useRef } from "react";
import { Filters } from "../Filters";
import { deleteViewQuery } from "../../../queries/views";
import { SaveUserWidgetPreferences } from "../../../queries/SaveUserPreference";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../StrictModeDroppable";
import reorder from "../../../../public/assets/icons/reorder.svg";
import { MySwitch } from "../../atoms/checkbox";
import { Close } from "@mui/icons-material";
import ChevronUp from "../../../../public/assets/icons/chevron-up.svg";
import ChevronDown from "../../../../public/assets/icons/chevron-down.svg";


interface ViewsConfig {
    [viewId: string]: {
        isHidden: boolean;
        position: number;
    };
}

type Option = {
    id: string;
    field: string;
    headerName: string;
    hide: boolean;
    isMeta?: boolean;
};

type Props = {
    anchorEl: any;
    handleClose: any;
    filters: any;
    setFilters: any;
    directoryType: string;
    widgetType: string;
    client: any;
    handleSaveView: Function;
    options: Option[];
    onHide: any;
    onSelect: any;
    existingView?: any;
    views: ViewType[];
    roles?: any[];
    allCoders?: any[];
    allGroups?: any[];
    queues?: any[];
    refreshViews: Function;
};

const defaultViewFilters = [
    {
        filterIndex: 0,
        fieldName: "workitem.group_id",
        fieldId: "workitem.group_id",
        operator: "in",
        value: "MY_GROUPS",
        join: undefined,
    },
];

const defaultCqaViewFilters = [
    {
        filterIndex: 0,
        fieldName: "case_type",
        fieldId: "case_type",
        operator: "eq",
        value: "CQA",
        join: undefined,
    },
];

export const managerRoles = ["coding_manager", "cqa_manager"];

const sortViewsByPosition = (views: ViewType[]): ViewType[] => {
    return [...views].sort((a, b) => {
        const getUserPref = (view) =>
            Array.isArray(view.userPreferences)
                ? view.userPreferences[0]
                : view.userPreferences;
        const positionA = getUserPref(a)?.position ?? Number.MAX_SAFE_INTEGER;
        const positionB = getUserPref(b)?.position ?? Number.MAX_SAFE_INTEGER;

        if (positionA !== positionB) {
            return positionA - positionB;
        }
        return 0;
    });
};
const selectStyles = {
  minHeight: "37px",
  "& .MuiSelect-icon": {
    backgroundImage: `url(${ChevronDown})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    color: "transparent",
  },
  "& .MuiSelect-select": {
    padding: "0 16px !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: `1px solid ${theme.palette.artisticStroke200.main} !important`,
  },
  "&.Mui-focused fieldset": {
    border: `1px solid ${theme.palette.artisticGrey400.main} !important`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: `${theme.palette.artisticGrey400.main} !important`,
  },
};

export const AddViewPopup = ({
                                 anchorEl,
                                 handleClose,
                                 client,
                                 handleSaveView,
                                 options,
                                 onHide,
                                 onSelect,
                                 existingView,
                                 views,
                                 roles,
                                 allCoders,
                                 allGroups,
                                 directoryType,
                                 widgetType,
                                 queues,
                                 refreshViews,
                                 ...props
                             }: Props) => {
    const [selected, setSelected] = React.useState(-1);
    const { setOptionsDict, optionsDict, detailGridOptions } =
        React.useContext(caseContext);
    const { userContext } = React.useContext(policiesContext);
    const [filters, setFilters] = React.useState<any>([]);
    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [values, setValues] = React.useState<Option[]>(
        options?.map((option) => {
            option.hide = false;
            return option;
        })
    );
    const [isGlobal, setIsGlobal] = React.useState(false);
    const [isEditable, setIsEditable] = React.useState(true);
    const [isDeletable, setIsDeletable] = React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [reorderedViews, setReorderedViews] = React.useState<ViewType[]>(() =>
        sortViewsByPosition(views)
    );
    const [defaultSortField, setDefaultSortField] =  React.useState<string>("date_of_service");
    const [defaultSortDirection, setDefaultSortDirection] = React.useState<"ASC" | "DESC">("ASC");
    const visibleColumns = React.useMemo(() => {
        if (!Array.isArray(values)) return [];
            return values.filter((v) => !v.hide);
        }, [values]);
  
    let filtersRef = useRef(undefined);
    filtersRef.current = filters;

    const reorderArray = (
        list: any[],
        startIndex: number,
        endIndex: number
    ): any[] => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    // Common function to save widget preferences
    const saveWidgetPreferences = async (updatedViews: ViewType[]) => {
        const viewsConfig: ViewsConfig = {};
        updatedViews.forEach((view, index) => {
            if (view.viewId) {
                const currentUserPrefs = Array.isArray(view.userPreferences)
                    ? view.userPreferences[0]
                    : view.userPreferences;

                viewsConfig[view.viewId] = {
                    isHidden: currentUserPrefs?.isHidden ?? false,
                    position: currentUserPrefs?.position ?? index + 1,
                };
            }
        });

        const payload = {
            request: {
                config: viewsConfig,
                healthSystemId: userContext?.activeContext?.healthSystemTenantId,
                service:
                    serviceLineMapper[userContext?.serviceLine?.name] ??
                    userContext?.serviceLine?.name,
                widgetType: "view",
            },
        };

        try {
            await client.mutate({
                mutation: SaveUserWidgetPreferences,
                variables: payload,
                fetchPolicy: "no-cache",
            });

            if (refreshViews) {
                await refreshViews();
            }
        } catch (error) {
            console.error("Failed to save widget preferences:", error);
            console.error(
                "Error details:",
                error.networkError?.result || error.graphQLErrors
            );
            throw error; // Re-throw to allow calling functions to handle the error
        }
    };

    const handleToggleViewVisibility = async (
        viewId: string,
        currentHiddenState: boolean
    ) => {
        const newHiddenState = !currentHiddenState;

        const updatedViews = reorderedViews.map((view, index) => {
            if (view.viewId === viewId) {
                const currentUserPrefs = Array.isArray(view.userPreferences)
                    ? view.userPreferences[0]
                    : view.userPreferences;

                return {
                    ...view,
                    userPreferences: {
                        ...currentUserPrefs,
                        isHidden: newHiddenState,
                        position: currentUserPrefs?.position ?? index + 1,
                    },
                };
            }
            return view;
        });

        setReorderedViews(updatedViews);

        try {
            await saveWidgetPreferences(updatedViews);
        } catch (error) {
            // Revert the local state on error
            setReorderedViews(reorderedViews);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        const newViews = reorderArray(
            reorderedViews,
            result.source.index,
            result.destination.index
        );

        let newSelected = selected;
        if (selected === result.source.index) {
            newSelected = result.destination.index;
        } else if (
            selected > result.source.index &&
            selected <= result.destination.index
        ) {
            newSelected = selected - 1;
        } else if (
            selected < result.source.index &&
            selected >= result.destination.index
        ) {
            newSelected = selected + 1;
        }

        const updatedViews = newViews.map((view, index) => {
            const currentUserPrefs = Array.isArray(view.userPreferences)
                ? view.userPreferences[0]
                : view.userPreferences;

            return {
                ...view,
                userPreferences: {
                    ...currentUserPrefs,
                    position: index + 1,
                    isHidden: currentUserPrefs?.isHidden ?? false,
                },
            };
        });

        setReorderedViews(updatedViews);
        setSelected(newSelected);
        getRUM().addAction('~worklist_side_nav_reorder_view', {});
                      

        try {
            await saveWidgetPreferences(updatedViews);
        } catch (error) {
            // Revert the local state on error
            setReorderedViews(reorderedViews);
            setSelected(selected);
        }
    };

    const setSelectedView = (index: any) => {
        if (index === selected || index === -1) {
            setSelected(-1);
            setName("");
            setDescription("");
            setIsGlobal(false);
            setIsEditable(true);
            setValues(
                options?.map((option) => {
                    return { ...option, hide: false };
                })
            );
            setFilters([]);
            setDefaultSortField("date_of_service");
            setDefaultSortDirection("ASC");
        } else if (index < reorderedViews.length) {
            setSelected(index);
            setName(reorderedViews[index].name);
            setDescription(reorderedViews[index].description);
            setValues(() => {
                const viewColumnFields =
                    reorderedViews[index].columns?.map((column) => column.field) || [];
                const orderedVisibleColumns =
                    reorderedViews[index].columns?.flatMap((column) => {
                        const option = options?.find((opt) => opt.field === column.field);
                        return option ? [{ ...option, hide: false }] : [];
                    }) || [];
                const hiddenColumns =
                    options
                        ?.filter((option) => !viewColumnFields.includes(option.field))
                        .map((option) => ({ ...option, hide: true })) || [];

                return [...orderedVisibleColumns, ...hiddenColumns];
            });
            const newFilters = convertFilters(reorderedViews[index].filters);

            setFilters(newFilters);

            const global = reorderedViews[index].view_type === "global";
            setIsGlobal(global);
             setDefaultSortField(
      reorderedViews[index]?.sort_order?.fieldName ?? ""
    );
    setDefaultSortDirection(
      reorderedViews[index]?.sort_order?.order ?? "ASC"
    );
        }
    };

    const convertFilter = (filter, combinator) => {
        let filterList: any[] = [];
        if (filter?.filters && filter.combinator) {
            filter.filters.forEach((deeperFilter, i) => {
                const deepestFilter = convertFilter(
                    deeperFilter,
                    i === 0 ? undefined : filter.combinator
                );
                filterList = filterList.concat(deepestFilter);
            });
        } else {
            const convertedFilter = {
                fieldId: filter.fieldId,
                fieldName: filter.key,
                operator: filter.operation,
                value: filter.value,
                join: combinator,
            };

            filterList.push(convertedFilter);
        }

        return filterList;
    };

    const convertFilters = (filters) => {
        let newFilters = [];

        if (!filters || !filters.filters) {
            filters = { filters: filters || [] };
        }

        filters?.filters?.forEach((filter, i) => {
            newFilters = newFilters.concat(
                convertFilter(filter, i !== 0 ? filters.combinator : undefined)
            );
        });

        newFilters = newFilters.map((newFilter, i) => {
            return { ...newFilter, filterIndex: i };
        });

        return newFilters;
    };

    const handleSave = (config) => {
        let newFilters = filters;

        for (let defaultFilter of defaultViewFilters) {
            const exists = newFilters.some(
                (filter) => filter.fieldName === defaultFilter.fieldName
            );
            if (!exists) {
                newFilters.unshift(defaultFilter);
            }
        }

        if (widgetType === WidgetConstants.CQA) {
            for (let defaultFilter of defaultCqaViewFilters) {
                const exists = newFilters.some(
                    (filter) => filter.fieldName === defaultFilter.fieldName
                );
                if (!exists) {
                    newFilters.unshift(defaultFilter);
                }
            }
        }

        for (let i = 0; i < newFilters.length; i++) {
            newFilters[i].filterIndex = i;
            newFilters[i].join = i === 0 ? undefined : newFilters[i].join ?? "AND";
        }

         const newConfig = {
            ...config,
            filters: buildSearchFilter(newFilters),
            sort_order: config.defaultSortField
                ? {
                    fieldName: config.defaultSortField,
                    order: config.defaultSortDirection,
                }
                : undefined,
        };
        handleSaveView({ ...newConfig, isGlobal, isEdit: selected !== -1 });
        setSelectedView(-1);
    };

    React.useEffect(() => {
        setValues(
            options?.map((option) => {
                option.hide = false;
                return option;
            })
        );
    }, [options]);

    React.useEffect(() => {
        const isManager = roles?.some((role) => managerRoles.includes(role));
        const isEditable =
            (reorderedViews[selected]?.editable ?? true) && (!isGlobal || isManager);
        const deleteable =
            userContext?.user?.userId === reorderedViews[selected]?.createdby &&
            isEditable === true &&
            selected > -1
                ? true
                : false;
        setIsDeletable(deleteable);
        setIsEditable(isEditable);
    }, [isGlobal, roles, reorderedViews, selected]);

    React.useEffect(() => {
        const sortedViews = sortViewsByPosition(views);
        setReorderedViews(sortedViews);
    }, [views]);

    const deleteView = async (viewId?: string) => {
        if (!viewId) return;

        try {
            await client.mutate({
                mutation: deleteViewQuery,
                variables: {
                    id: viewId,
                },
            });
        } catch (error) {
            console.error("Failed to delete view:", error);
        }
    };

    const handleDeleteConfirm = async () => {
        await deleteView(reorderedViews[selected]?.viewId);
        await refreshViews();
        setShowDeleteDialog(false);
        setSelectedView(0);
    };

    const getViewHiddenState = (view: ViewType): boolean => {
        const userPrefs = view.userPreferences;
        return userPrefs?.isHidden ?? false;
    };

    return (
        <div className="viewPopup">
            <Popover
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                PaperProps={{
                    style: {
                        width: "80%",
                        height: "75vh",
                    },
                }}
            >
                <Grid container direction="row">
                    <Grid item xs={12}>
                        <TabContext value={selected === -1 ? "Add View" : "Saved Views"}>
                            <Box
                                sx={{
                                    display: "flex",
                                    borderBottom: "1px solid lightgray",
                                    paddingLeft: theme.spacing(3),
                                }}
                            >
                                <TabList
                                    sx={{
                                        "& .Mui-selected": {
                                            color: theme.palette.artisticTextInteractive.main,
                                        },
                                    }}
                                    variant="scrollable"
                                >
                                    <Tab
                                        style={{ minWidth: "76px", padding: "6px 32px" }}
                                        label={
                                            <Typography variant="body1">{"Saved Views"}</Typography>
                                        }
                                        value={"Saved Views"}
                                        onClick={() => {
                                            setSelectedView(0);
                                        }}
                                    />
                                    <Tab
                                        style={{ minWidth: "76px", padding: "6px 32px" }}
                                        label={
                                            <Typography variant="body1">{"+ Add View"}</Typography>
                                        }
                                        value={"Add View"}
                                        onClick={() => {
                                            setSelectedView(-1);
                                        }}
                                    />
                                </TabList>
                            </Box>
                        </TabContext>
                    </Grid>
                    <Grid item container xs={12} style={{ height: "calc(75vh - 50px)" }}>
                        <Grid
                            item
                            xs={4}
                            style={{
                                height: "100%",
                                overflow: "auto",
                                borderRight: "1px solid lightgray",
                            }}
                        >
                            <div className="existingViews">
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        pb: "24px",
                                    }}
                                >
                                    <Box sx={{ flex: 1, overflow: "auto" }}>
                                        <DragDropContext onDragEnd={handleDragEnd}>
                                            <StrictModeDroppable droppableId="views-list">
                                                {(provided) => (
                                                    <div
                                                        {...provided.droppableProps}
                                                        ref={provided.innerRef}
                                                    >
                                                        {reorderedViews.map((view, i) => {
                                                            const isHidden = getViewHiddenState(view);

                                                            return (
                                                                <Draggable
                                                                    key={`view-${view.viewId || view.name}`}
                                                                    draggableId={(
                                                                        view.viewId || view.name
                                                                    ).toString()}
                                                                    index={i}
                                                                    isDragDisabled={false}
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <Box
                                                                            key={view?.viewId || view?.name}
                                                                            sx={{
                                                                                maxHeight: "350px",
                                                                                minWidth: "150px",
                                                                                overflowY: "scroll",
                                                                                pl: "16px",
                                                                                pr: "16px",
                                                                                ...scrollStyles().scrollBar,
                                                                            }}
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            style={{
                                                                                ...provided.draggableProps.style,
                                                                                backgroundColor: snapshot.isDragging
                                                                                    ? theme.palette.action.hover
                                                                                    : "transparent",
                                                                            }}
                                                                        >
                                                                            <Tooltip
                                                                                title={`${view.name} (Position: ${
                                                                                    Array.isArray(view.userPreferences)
                                                                                        ? view.userPreferences[0]
                                                                                        ?.position ?? "default"
                                                                                        : view.userPreferences?.position ??
                                                                                        "default"
                                                                                })`}
                                                                                arrow
                                                                                placement="right"
                                                                            >
                                                                                <MenuItem
                                                                                    sx={{
                                                                                        ":hover": {
                                                                                            backgroundColor: "transparent",
                                                                                        },
                                                                                        padding: "8px 16px",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        border: "2px solid",
                                                                                        borderColor:
                                                                                        theme.palette.artisticGrey100
                                                                                            .main,
                                                                                        margin: "16px 0px 0px",
                                                                                        height: "40px",
                                                                                    }}
                                                                                    selected={i === selected}
                                                                                    disableRipple
                                                                                    value={view?.name}
                                                                                    onKeyDown={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <Box
                                                                                        {...provided.dragHandleProps}
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                        justifyContent="center"
                                                                                        sx={{
                                                                                            height: "37px",
                                                                                            width: "30px",
                                                                                            cursor: "grab",
                                                                                            ml: "-12px",
                                                                                            mt: "-4px",
                                                                                            opacity: 1,
                                                                                            "&:active": {
                                                                                                cursor: "grabbing",
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <img src={reorder} alt="reorder" />
                                                                                    </Box>
                                                                                    <Switch
                                                                                        checked={!isHidden}
                                                                                        onChange={() =>
                                                                                            handleToggleViewVisibility(
                                                                                                view.viewId,
                                                                                                isHidden
                                                                                            )
                                                                                        }
                                                                                        disabled={!view.editable}
                                                                                        sx={{
                                                                                            mt: "-4px",
                                                                                            "& .MuiSwitch-switchBase": {
                                                                                                marginTop: "4px",
                                                                                                marginLeft: !isHidden
                                                                                                    ? "-1px"
                                                                                                    : "4px",
                                                                                            },
                                                                                            "& .MuiSwitch-thumb": {
                                                                                                boxShadow:
                                                                                                    "0 2px 4px 0 rgba(0, 0, 0, 0.25)",
                                                                                                height: "16px",
                                                                                                width: "16px",
                                                                                            },
                                                                                            "& .MuiSwitch-track": {
                                                                                                borderRadius: "10px",
                                                                                                height: "18px",
                                                                                                opacity: "1 !important",
                                                                                                backgroundColor: !isHidden
                                                                                                    ? theme.palette
                                                                                                        .artisticPrimary100.main
                                                                                                    : theme.palette
                                                                                                        .artisticStroke200.main,
                                                                                            },
                                                                                            "&.Mui-checked .MuiSwitch-thumb":
                                                                                                {
                                                                                                    backgroundColor: "#FFFFFF",
                                                                                                },
                                                                                            "& .MuiSwitch-switchBase.Mui-checked":
                                                                                                {
                                                                                                    color: "#FFFFFF",
                                                                                                    "&:hover": {
                                                                                                        backgroundColor:
                                                                                                            "rgba(0, 122, 255, 0.04)",
                                                                                                    },
                                                                                                },
                                                                                            opacity: !view.editable ? 0.6 : 1,
                                                                                            cursor: !view.editable
                                                                                                ? "not-allowed"
                                                                                                : "pointer",
                                                                                        }}
                                                                                    />
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: "flex",
                                                                                            gap: "5px",
                                                                                            justifyContent: "space-between",
                                                                                            flex: 1,
                                                                                        }}
                                                                                    >
                                                                                        <Button
                                                                                            sx={{
                                                                                                justifyContent: "start",
                                                                                                width: "100%",
                                                                                                color:
                                                                                                theme.palette
                                                                                                    .artisticPrimary100.main,
                                                                                            }}
                                                                                            disableRipple
                                                                                            onClick={() => setSelectedView(i)}
                                                                                        >
                                                                                            <Typography variant="body1">
                                                                                                {view.name}
                                                                                            </Typography>
                                                                                            {view.view_type === "global" ? (
                                                                                                <img
                                                                                                    src={global}
                                                                                                    alt="global"
                                                                                                    className="global-icon"
                                                                                                />
                                                                                            ) : null}
                                                                                        </Button>
                                                                                    </Box>
                                                                                </MenuItem>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    )}
                                                                </Draggable>
                                                            );
                                                        })}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </StrictModeDroppable>
                                        </DragDropContext>
                                    </Box>
                                </Box>
                            </div>
                        </Grid>
                        <Grid item xs={8} style={{ height: "100%", overflow: "auto" }}>
                            <Box
                                sx={{
                                    minHeight: "50px",
                                    margin: "20px",
                                    overflow: "auto",
                                    flex: 1,
                                    marginRight: 0,
                                    minWidth: "550px",
                                }}
                            >
                                <Box>
                                    <Typography variant="h6" sx={{ mb: "10px" }}>
                                        {selected !== -1 ? "Edit" : "Add"} View
                                    </Typography>
                                    <Divider
                                        sx={{
                                            bgcolor: theme.palette.artisticStroke100.main,
                                            mb: "20px",
                                        }}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="column">
                                    <Typography
                                        variant="caption2"
                                        color={theme.palette.artisticTextHigh.main}
                                    >
                                        Name
                                    </Typography>
                                    <Input
                                        sx={{
                                            marginBottom: "20px",
                                            marginTop: "4px",
                                            padding: "8px 12px",
                                            border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                            borderRadius: "4px",
                                        }}
                                        disabled={!isEditable}
                                        placeholder="Enter view name"
                                        required
                                        onChange={(event) => {
                                            setName(event.target.value);
                                        }}
                                        disableUnderline
                                        value={name}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="column">
                                    <Typography
                                        variant="caption2"
                                        color={theme.palette.artisticTextHigh.main}
                                    >
                                        Description
                                    </Typography>
                                    <TextField
                                        disabled={!isEditable}
                                        sx={{
                                            marginBottom: "20px",
                                            marginTop: "4px",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                                },
                                                "&:hover fieldset": {
                                                    border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                                },
                                                "&.Mui-focused fieldset": {
                                                    border: `1px solid ${theme.palette.artisticStroke200.main}`,
                                                },
                                            },
                                        }}
                                        placeholder="Enter a description about the view"
                                        minRows={3}
                                        multiline
                                        value={description}
                                        onChange={(event) => {
                                            setDescription(event.target.value);
                                        }}
                                    />
                                </Box>
                                <Switch
                                    disabled={!isEditable}
                                    defaultChecked
                                    size="small"
                                    checked={isGlobal}
                                    onChange={() => setIsGlobal(!isGlobal)}
                                />
                                Global View
                                <div
                                    style={{
                                        borderTop: `1px solid ${theme.palette.artisticStroke200.main}`,
                                        marginTop: "10px",
                                    }}
                                >
                                    <Typography style={{ paddingTop: "10px" }}>
                                        Filters
                                    </Typography>

                                    <Filters
                                        client={client}
                                        directoryType={directoryType}
                                        detailGridOptions={detailGridOptions}
                                        // @ts-expect-error columnDefs items do not match the expected columnOptions type
                                        columnOptions={(detailGridOptions?.columnDefs ?? []).filter(
                                            (col) => !defaultColumns.includes(getColId(col))
                                        )}
                                        optionsDict={optionsDict}
                                        setOptionsDict={setOptionsDict}
                                        filters={filters}
                                        setFilters={setFilters}
                                        allCoders={allCoders}
                                        allGroups={allGroups}
                                        queues={queues}
                                        hasBorder={false}
                                        isEditable={isEditable}
                                    />
                                </div>
                                <Box
                                    sx={{
                                        borderTop: `1px solid ${theme.palette.artisticStroke200.main}`,
                                        paddingTop: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                                        Fields
                                    </Typography>
                                </Box>
                                                            <Box
                                    sx={{
                                        borderRadius: "4px",
                                        marginBottom: "20px",
                                        marginLeft: "-15px",
                                    }}
                                >
                                    <FieldsMenu
                                        disabled={!isEditable}
                                        anchorEl={undefined}
                                        options={options}
                                        values={values}
                                        setValues={setValues}
                                    />
                                </Box>
                                <Box
                                sx={{
                                    borderTop: `1px solid ${theme.palette.artisticStroke200.main}`,
                                    paddingTop: "10px",
                                    marginBottom: "20px",
                                }}
                                >
                                <Typography variant="body1" sx={{ marginBottom: "16px" }}>
                                    Default Sort
                                </Typography>

                                {/* Sort By */}
                                <Box
                                    sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 2,
                                    }}
                                >
                                    <Typography
                                    variant="caption2"
                                    sx={{
                                        width: "120px",   
                                        color: theme.palette.artisticTextHigh.main,
                                    }}
                                    >
                                    Sort By
                                    </Typography>

                                    <Select
                                    disabled={!isEditable}
                                    value={defaultSortField}
                                    onChange={(e) => setDefaultSortField(e.target.value)}
                                    sx={{
                                        width: "220px",
                                        ...selectStyles,
                                    }}
                                    >
                                    {visibleColumns.map((col) => (
                                        <MenuItem key={col.field} value={col.field}>
                                        {col.headerName}
                                        </MenuItem>
                                    ))}
                                    </Select>
                                </Box>

                                {/* Direction */}
                                <Box
                                    sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    }}
                                >
                                    <Typography
                                    variant="caption2"
                                    sx={{
                                        width: "120px",
                                        color: theme.palette.artisticTextHigh.main,
                                    }}
                                    >
                                    Direction
                                    </Typography>

                                    <Select
                                    disabled={!isEditable}
                                    value={defaultSortDirection}
                                    onChange={(e) =>
                                        setDefaultSortDirection(e.target.value as "ASC" | "DESC")
                                    }
                                    sx={{
                                        width: "220px",
                                        ...selectStyles,
                                    }}
                                    >
                                    <MenuItem value="ASC">Ascending</MenuItem>
                                    <MenuItem value="DESC">Descending</MenuItem>
                                    </Select>
                                </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row-reverse",
                                        gap: "20px",
                                        padding: "0 20px 20px 0",
                                    }}
                                >
                                    {isEditable && name?.trim()?.length > 0 && (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                getRUM().addAction(
                                                    selected !== -1
                                                        ? '~worklist_side_nav_update_view_clicked'
                                                        : '~worklist_side_nav_save_view_clicked',
                                                    {}
                                                );

                                                handleSave({
                                                    name,
                                                    description,
                                                    filters: buildSearchFilter(filters),
                                                    columns: values?.filter((col) => col.hide === false),
                                                    viewId: reorderedViews[selected]?.viewId,
                                                    defaultSortField,
                                                    defaultSortDirection,
                                                });
                                                handleClose();
                                            }}
                                        >
                                            Save
                                        </Button>
                                    )}

                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedView(-1);
                                            handleClose();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {isDeletable && (
                                        <Button
                                            variant="text"
                                            onClick={async () => {
                                                setShowDeleteDialog(true);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Box>
                                <Dialog
                                    open={showDeleteDialog}
                                    onClose={() => setShowDeleteDialog(false)}
                                    aria-labelledby="delete-dialog-title"
                                    aria-describedby="delete-dialog-description"
                                >
                                    <DialogTitle
                                        id="delete-dialog-title"
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            pr: 1,
                                        }}
                                    >
                                        Confirm view deletion
                                        <IconButton
                                            aria-label="close"
                                            onClick={() => setShowDeleteDialog(false)}
                                            sx={{
                                                position: "absolute",
                                                right: 8,
                                                top: 10,
                                                color: (theme) => theme.palette.grey[500],
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent>
                                        <DialogContentText id="delete-dialog-description">
                                            Are you sure you want to delete this view?
                                            {reorderedViews[selected]?.view_type === "global" &&
                                                " The view is shared with others and they will no longer be able to see it."}
                                        </DialogContentText>
                                    </DialogContent>
                                    <DialogActions sx={{ padding: "16px  24px" }}>
                                        <Button
                                            onClick={() => setShowDeleteDialog(false)}
                                            variant="outlined"
                                        >
                                            No, Cancel
                                        </Button>
                                        <Button onClick={handleDeleteConfirm} variant="contained">
                                            Yes, Delete
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Popover>
        </div>
    );
};
